// src/components/ContinuityPlanPanel.js
//
// 6a-11 (2026-09-14, task list section 229). The continuity plan OFFERED to a General Chairperson four
// ways (ruling 18; requirements SS6.4), and his choice recorded on committees/{committee}.continuityPlan.
// Every rule of who-may and what-shape is src\data\continuityPlan.js; this draws and writes.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { CHOICES, TEMPLATE, NOTE_MAX, mayRecord, continuityRecord, continuityLine } from '../data/continuityPlan';
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function ContinuityPlanPanel({ user, committee }) {
  const [record, setRecord] = useState(undefined);
  const [form, setForm] = useState({ choice: '', borrowedFrom: '', note: '' });
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!committee) return undefined;
    const unsub = onSnapshot(doc(db, 'committees', committee), s => { setRecord(s.exists() ? (s.data().continuityPlan || null) : null); setErr(''); },
      e => { setErr('Could not read the committee record: ' + (e && e.code ? e.code : e.message)); setRecord(null); });
    return () => unsub();
  }, [committee]);
  if (!committee) return null;
  const may = mayRecord(user, committee);
  const save = async () => {
    setErrors([]);
    const r = continuityRecord(user, committee, form);
    if (!r.ok) { setErrors(r.errors); return; }
    setSaving(true);
    try { await updateDoc(doc(db, 'committees', committee), r.patch); setForm({ choice: '', borrowedFrom: '', note: '' }); }
    catch (e) { setErrors(['Not recorded: ' + (e && e.code ? e.code : e.message)]); }
    setSaving(false);
  };
  return (
    <div style={{ border: '1px solid ' + (record ? C.line : C.accent), background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Continuity of office - {committee}</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Every General Chairperson should have a continuity plan, offered four ways - adopt this one, write your own, borrow another committee's, or have none. The platform offers; it does not impose. Template: {TEMPLATE.title} (data\YCP_GC_CONTINUITY_PLAN_2026-08-21.md in the repository).<RulingRef user={user} n={18} why="Every General Chairperson should have a continuity plan; the platform offers one and does not impose it." /></div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: record ? C.ok : C.accent, whiteSpace: 'nowrap' }}>{record === undefined ? '…' : record ? (CHOICES.find(c => c.key === record.choice) || {}).label || record.choice : 'not yet considered'}</div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim }}>
        {err ? <div style={{ color: C.bad }}>&#9888; {err}</div> : null}
        {record !== undefined ? <div style={{ color: C.text, marginBottom: 10 }}>{continuityLine(record)}</div> : null}
        {may ? (
          <div style={{ borderTop: '1px solid ' + C.line, paddingTop: 10 }}>
            <div style={{ color: C.text, fontWeight: 700, marginBottom: 6 }}>{record ? 'Change the committee\'s choice' : 'Record the committee\'s choice'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <select aria-label="Where the committee keeps its continuity plan" className="form-select" value={form.choice} onChange={e => setForm({ ...form, choice: e.target.value })}>
                <option value="">Choose…</option>
                {CHOICES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              {form.choice === 'borrowed' ? <input aria-label="Committee followed (GO-nnn)" className="form-input" placeholder="Committee followed (GO-nnn)" value={form.borrowedFrom} onChange={e => setForm({ ...form, borrowedFrom: e.target.value.toUpperCase() })} /> : <div />}
              <textarea aria-label="Note on the continuity plan" className="form-input" style={{ gridColumn: '1 / span 2' }} rows={2} maxLength={NOTE_MAX} placeholder={form.choice === 'own' ? 'Where the committee\'s own plan is kept, or that it is still to be written (required)' : 'Note (optional)'} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
              <button className="btn btn-primary btn-sm" disabled={saving || !form.choice} onClick={save}>Record</button>
            </div>
            {errors.length ? <div style={{ color: C.bad, marginTop: 6 }}>{errors.map((e, i) => <div key={i}>&#9888; {e}</div>)}</div> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
